import {
  buildBooleanFilter,
  buildBucketFilter,
  buildFilter,
  buildRangeFilter,
  composeFilters,
} from '@iheartjane/menu';

import type { AlgoliaConfig } from './client';
import { getAlgoliaClient } from './client';
import type { CurrentSort } from './currentSort';
import type { SearchOptions } from './types/searchOptions';
import type { SearchResponse } from './types/searchResponse';

type Filters<T> = {
  [Attribute in keyof T]?: string[] | number[];
};

type BooleanFilters<T> = {
  [Attribute in keyof T]?: boolean;
};

type BucketFilters<T> = {
  [Attribute in keyof T]?: string[];
};

type RangeFilters<T> = {
  [Attribute in keyof T]?: {
    max?: number;
    min?: number;
  };
};

export interface SearchProps<T> {
  booleanFilters?: BooleanFilters<T>;
  bucketFilters?: BucketFilters<T>;
  currentSort?: CurrentSort;
  enabled?: boolean;
  facets?: string[];
  filters?: Filters<T>;
  hitsPerPage?: number | string;
  indexPrefix: string;
  options?: SearchOptions;
  page?: number | string;
  rangeFilters?: RangeFilters<T>;
  searchText?: string | null;
  staticFilters?: string;
}

export const searchForHits = async <T>(
  config: AlgoliaConfig,
  {
    booleanFilters,
    bucketFilters,
    currentSort,
    facets,
    filters,
    hitsPerPage,
    indexPrefix,
    options,
    page,
    rangeFilters,
    searchText,
    staticFilters,
  }: SearchProps<T>
): Promise<SearchResponse<T>> => {
  const client = getAlgoliaClient(config);
  const prefix = currentSort
    ? `${indexPrefix}${currentSort.suffix}`
    : indexPrefix;
  const indexName = `${prefix}${config.algoliaEnv}`;

  const composedFilters = composeFilters(
    ...(staticFilters ? [staticFilters] : []),
    ...(filters
      ? Object.keys(filters).map((attribute) =>
          buildFilter(attribute, filters[attribute as keyof T])
        )
      : []),
    ...(rangeFilters
      ? Object.keys(rangeFilters).map((attribute) => {
          const value = rangeFilters[attribute as keyof T];

          return buildRangeFilter(attribute, value?.min, value?.max);
        })
      : []),
    ...(bucketFilters
      ? Object.keys(bucketFilters).map((attribute) => {
          const value = bucketFilters[attribute as keyof T];

          return buildBucketFilter(attribute, value);
        })
      : []),
    ...(booleanFilters
      ? Object.keys(booleanFilters).map((attribute) => {
          const value = booleanFilters[attribute as keyof T];

          return buildBooleanFilter(attribute, value);
        })
      : [])
  );

  const query = searchText ?? '';
  return await client.searchSingleIndex<T>({
    indexName,
    searchParams: {
      query,
      filters: composedFilters,
      ...(hitsPerPage && { hitsPerPage: Number(hitsPerPage) }),
      ...(page && { page: Number(page) }),
      ...(facets && facets.length > 0 && { facets }),
      ...options,
    },
  });
};
